import React, { useState } from 'react';
import { Check, X, ChevronDown } from 'lucide-react';
import { ROLE_FAMILIES, SENIORITY_BANDS } from '../lib/profileInference';

export default function ProfileSummary({ profile, onConfirm, onSkip }) {
  // profile = { role_family, seniority_band, skills, remote_preference }
  const [roleFamily, setRoleFamily] = useState(profile?.role_family || 'Other');
  const [seniority, setSeniority] = useState(profile?.seniority_band || 'mid');
  const [skills, setSkills] = useState(profile?.skills || []);
  const [remotePref, setRemotePref] = useState(profile?.remote_preference || 'any');
  const [newSkill, setNewSkill] = useState('');

  const handleAddSkill = () => {
    const skill = newSkill.trim();
    if (skill && !skills.includes(skill) && skills.length < 10) {
      setSkills([...skills, skill]);
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setSkills(skills.filter(s => s !== skillToRemove));
  };

  const handleConfirm = () => {
    onConfirm({ role_family: roleFamily, seniority_band: seniority, skills, remote_preference: remotePref });
  };

  const remoteOptions = [
    { value: 'any', label: 'Any' },
    { value: 'remote', label: 'Remote' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'onsite', label: 'Onsite' },
  ];

  // Format seniority for display
  const formatSeniority = (band) => {
    const map = { intern: 'Intern', junior: 'Junior', mid: 'Mid-Level', senior: 'Senior', lead: 'Lead', director: 'Director', vp: 'VP', c_level: 'C-Level' };
    return map[band] || band;
  };

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4">
        <h2 className="text-white font-bold">Your Profile Summary</h2>
        <p className="text-cyan-100 text-xs mt-0.5">Review your inferred profile — make corrections if needed</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Role Family */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Role Family</label>
          <div className="relative">
            <select
              value={roleFamily}
              onChange={(e) => setRoleFamily(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm appearance-none focus:border-vouch-teal focus:outline-none bg-white"
            >
              {ROLE_FAMILIES.map(rf => (
                <option key={rf} value={rf}>{rf}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Seniority */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Seniority Level</label>
          <div className="relative">
            <select
              value={seniority}
              onChange={(e) => setSeniority(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm appearance-none focus:border-vouch-teal focus:outline-none bg-white"
            >
              {SENIORITY_BANDS.map(sb => (
                <option key={sb} value={sb}>{formatSeniority(sb)}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Skills */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Skills</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {skills.map(skill => (
              <span key={skill} className="inline-flex items-center gap-1 text-xs bg-vouch-navy bg-opacity-10 text-vouch-navy px-2 py-1 rounded-full font-medium">
                {skill}
                <button onClick={() => handleRemoveSkill(skill)} className="hover:text-red-500" aria-label={`Remove ${skill}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          {skills.length < 10 && (
            <form onSubmit={(e) => { e.preventDefault(); handleAddSkill(); }} className="flex gap-2">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add a skill..."
                className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:border-vouch-teal focus:outline-none"
              />
              <button type="submit" disabled={!newSkill.trim()} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-50">
                Add
              </button>
            </form>
          )}
        </div>

        {/* Remote Preference */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Work Preference</label>
          <div className="flex gap-2">
            {remoteOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setRemotePref(opt.value)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  remotePref === opt.value
                    ? 'bg-vouch-teal text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button onClick={onSkip} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200">
            Skip
          </button>
          <button onClick={handleConfirm} className="flex-1 py-2.5 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5">
            <Check size={16} /> Looks Good
          </button>
        </div>
      </div>
    </div>
  );
}
